import axios from "axios";
import {
  getSignedUrl,
  getFileMetadata,
  getImageDerivativeManifest,
} from "../api/files";
import { Feed, TypeMapping } from "../types/api/Feed";
import { PostsMedia } from "../types/api/PostMedia";
import { ImageDerivative } from "../shared/imageDerivatives";

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const downloadFile = (blob: Blob, name = "download") => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

const sleep = (delay: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay);
  });

export async function download_files(
  files: {
    download: string;
    fallback?: string;
    filename: string;
  }[],
) {
  const failedFiles: string[] = [];

  for (let file of files) {
    try {
      const separatorIndex = file.download.lastIndexOf("/");
      const directory = file.download.slice(0, separatorIndex);
      const storedFilename = file.download.slice(separatorIndex + 1);
      const metadata = await getFileMetadata(directory, storedFilename);
      const TEN_MB = 10 * 1024 * 1024;
      const path =
        metadata?.size > TEN_MB && file.fallback
          ? file.fallback
          : file.download;

      const { data: blob } = await axios.get(await getSignedUrl(path), {
        responseType: "blob",
      });
      downloadFile(blob, file.filename);
      if (isSafari) {
        await sleep(3000);
      } else {
        await sleep(1500);
      }
    } catch (error) {
      console.error(`Failed to download ${file.filename}`, error);
      failedFiles.push(file.filename);
    }
  }

  if (failedFiles.length > 0) {
    throw new Error(
      `Failed to download ${failedFiles.length} file${
        failedFiles.length === 1 ? "" : "s"
      }: ${failedFiles.join(", ")}`,
    );
  }
}

type PhotoRequirement = {
  minetype?: string[];
  max_filesize?: number;
  max_items?: number;
  min_aspect_ratio?: number;
  max_aspect_ratio?: number;
  max_resolution?: number;
  overrides?: Record<string, Partial<PhotoRequirement>>;
};

type FeedRequirements = {
  PHOTO?: PhotoRequirement;
  total_max_items?: number;
};

function getPositiveNumber(value: unknown, field: string, feed: Feed) {
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number) || number <= 0) {
    throw new Error(`${feed.name} is missing a valid ${field} requirement`);
  }
  return number;
}

function getPhotoRequirement(feed: Feed, postType?: string) {
  const requirements = feed.requirements as FeedRequirements | null;
  const base = requirements?.PHOTO;
  if (!base) {
    throw new Error(`${feed.name} is missing photo requirements`);
  }

  const typeMapping = feed.type_mapping as TypeMapping | null;
  const mappedType = postType
    ? (typeMapping?.[postType]?.type ?? postType)
    : undefined;
  const override = mappedType ? base.overrides?.[mappedType] : undefined;

  return {
    ...base,
    ...override,
    minetype: override?.minetype ?? base.minetype,
  };
}

function appendFeedSuffix(
  filename: string,
  storedFilename: string,
  feed: Feed,
) {
  const separator = filename.lastIndexOf(".");
  const basename = separator > 0 ? filename.slice(0, separator) : filename;
  const storedExtension = storedFilename.match(/\.[^.]+$/)?.[0] ?? "";
  const feedSuffix = (feed.slug || feed.name || "feed")
    .trim()
    .replace(/[^a-z0-9_-]+/gi, "_");

  return `${basename}_${feedSuffix}${storedExtension}`;
}

export async function getFeedPhotoDownloads({
  feed,
  postType,
  media,
}: {
  feed: Feed;
  postType?: string;
  media: PostsMedia[];
}) {
  const requirements = feed.requirements as FeedRequirements | null;
  const photoRequirement = getPhotoRequirement(feed, postType);
  const maxFileSizeBytes =
    getPositiveNumber(
      photoRequirement.max_filesize,
      "PHOTO.max_filesize",
      feed,
    ) *
    1024 *
    1024;
  const maxResolution = getPositiveNumber(
    photoRequirement.max_resolution,
    "PHOTO.max_resolution",
    feed,
  );
  const minAspectRatio = getPositiveNumber(
    photoRequirement.min_aspect_ratio,
    "PHOTO.min_aspect_ratio",
    feed,
  );
  const maxAspectRatio = getPositiveNumber(
    photoRequirement.max_aspect_ratio,
    "PHOTO.max_aspect_ratio",
    feed,
  );
  const allowedMimeTypes = photoRequirement.minetype;

  if (!allowedMimeTypes || allowedMimeTypes.length === 0) {
    throw new Error(`${feed.name} is missing PHOTO.minetype requirements`);
  }
  if (minAspectRatio > maxAspectRatio) {
    throw new Error(`${feed.name} has invalid photo aspect-ratio requirements`);
  }

  const itemLimits = [photoRequirement.max_items, requirements?.total_max_items]
    .map((value) => (value === undefined ? null : Number(value)))
    .filter(
      (value): value is number =>
        value !== null && Number.isFinite(value) && value > 0,
    );
  const maxItems =
    itemLimits.length > 0 ? Math.floor(Math.min(...itemLimits)) : null;
  if (maxItems === null) {
    throw new Error(`${feed.name} is missing a photo item-limit requirement`);
  }
  if (media.length > maxItems) {
    throw new Error(
      `${feed.name} allows ${maxItems} photo${
        maxItems === 1 ? "" : "s"
      }, but ${media.length} are selected`,
    );
  }

  return Promise.all(
    media.map(async (item) => {
      if (!item.src_original) {
        throw new Error(`The original path is missing for ${item.title}`);
      }

      const manifest = await getImageDerivativeManifest(item.src_original);
      if (!manifest) {
        throw new Error(`Image derivatives are unavailable for ${item.title}`);
      }

      const candidates = Object.values(manifest.derivatives)
        .filter((candidate): candidate is ImageDerivative => {
          if (!candidate) return false;
          const aspectRatio = candidate.width / candidate.height;
          return (
            allowedMimeTypes.includes(candidate.mediaType) &&
            Math.max(candidate.width, candidate.height) <= maxResolution &&
            aspectRatio >= minAspectRatio &&
            aspectRatio <= maxAspectRatio
          );
        })
        .sort(
          (a, b) =>
            Number(b.hdr) - Number(a.hdr) ||
            Math.max(b.width, b.height) - Math.max(a.width, a.height),
        );

      for (const candidate of candidates) {
        const separatorIndex = candidate.path.lastIndexOf("/");
        const metadata = await getFileMetadata(
          candidate.path.slice(0, separatorIndex),
          candidate.path.slice(separatorIndex + 1),
        );
        if (typeof metadata?.size !== "number") {
          throw new Error(`File size is unavailable for ${item.title}`);
        }
        if (metadata.size <= maxFileSizeBytes) {
          const storedFilename = candidate.path.slice(separatorIndex + 1);
          return {
            download: candidate.path,
            filename: appendFeedSuffix(
              item.title || storedFilename,
              storedFilename,
              feed,
            ),
          };
        }
      }

      throw new Error(
        `No derivative of ${item.title} satisfies ${feed.name}'s photo requirements`,
      );
    }),
  );
}
